import { useMemo, useState } from 'react';
import {
  line, defaultSetPick, mergeFieldText, mergeSetConflicts, mergeSetPickable, setPicksByRecord, t,
  type DuplicateRecordFacts, type ItemMergeFacts, type MergeSetAnswer, type MergeSetPreviewAnswer, type MergeSetRecordType,
} from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { FormFrame } from '../../components/forms/FormFrame';
import { post, useApi } from '../records/api';
import { MOVE_TITLES, createdByOf, flagsOf } from './DuplicatesPage';
import styles from './DuplicatesPage.module.css';
import setStyles from './MergeSetWindow.module.css';
import { MergeTable } from './MergeTable';

/** The list's Merge press (decision 7450): the record type, the one that stays, and each record merged into it. */
export const DUPLICATES_MERGE_SET_PATH = '/duplicates/merge-set';
const DUPLICATES_MERGE_SET_PREVIEW_PATH = '/duplicates/merge-set-preview';
/** The Items list's Merge press and its window's read (decision 7470), on the Item module's own gate rather than the Duplicates report's. */
export const ITEM_MERGE_PATH = '/catalog/item-merge';
const ITEM_MERGE_PREVIEW_PATH = '/catalog/item-merge-preview';
/** The window's read (decisions 7450, 7470): every selected record, the survivor proposed, and what moves from each other one into the one named to stay. */
export const mergeSetPreviewPath = (recordType: MergeSetRecordType, ids: readonly string[], intoId: string | null): string =>
  recordType === 'Item'
    ? `${ITEM_MERGE_PREVIEW_PATH}?${new URLSearchParams({ ids: ids.join(','), ...(intoId ? { intoId } : {}) }).toString()}`
    : `${DUPLICATES_MERGE_SET_PREVIEW_PATH}?${new URLSearchParams({ recordType, ids: ids.join(','), ...(intoId ? { intoId } : {}) }).toString()}`;

/** What the list reads after a press: a notice, good or naming each record refused. */
export interface MergeSetNotice {
  tone: 'good' | 'error';
  text: string;
}

/** The notice a press answers with (decision 7450): how many merged, the request it raised, or each record refused with its reason. */
export function mergeSetNotice(answer: MergeSetAnswer): MergeSetNotice {
  if (answer.outcome === 'Requested') {
    // 🔒 One request per approver its pairs route to (decision 7450): the notice says when there are two.
    const reasons = answer.requests.flatMap((one) => (one.escalationReason ? [`· ${one.escalationReason}`] : []));
    return { tone: 'good', text: line(answer.requests.length > 1 ? t('duplicates.mergeSet.requestedTwo') : t('duplicates.merge.requested'), ...reasons) };
  }
  const merged = answer.pairs.filter((pair) => pair.merged).length;
  const refused = answer.pairs.flatMap((pair) => (pair.merged ? [] : [`${pair.name}: ${pair.refusal}`]));
  const done = line(merged, t('duplicates.mergeSet.merged'));
  return refused.length === 0 ? { tone: 'good', text: done } : { tone: 'error', text: line(done, t('duplicates.mergeSet.partial'), refused.join(' · ')) };
}

/**
 * 🔒 Merge the records selected on a list (piece 10; decision 7450): the merge
 * window of decision 2501 with a column per selected record, in the list's
 * order. The survivor is chosen in its column's head, the one decision 1484's
 * rule proposes across the set chosen first. Each field two or more records
 * fill differently is a row with the better value picked in its cell, the
 * survivor's first, else the first merged record's that fills it; an empty
 * cell takes no pick. What moves is named under each merged record's name.
 *
 * 🔒 A record posted ledger lines name cannot be merged away: its column says
 * so and Merge stands disabled with the reason. The press sends the set once;
 * the server merges each record in its own transaction, or raises one request.
 * Drawn through the Form frame (decision 3060), so a refusal stands inside it.
 * It walks nothing: Merge and Next, Skip and Not the Same are the report's own.
 */
export function MergeSetWindow({ recordType, ids, apiBaseUrl = '', onCancel, onMerged }: {
  recordType: MergeSetRecordType;
  /** The selected records, in the list's order. */
  ids: readonly string[];
  apiBaseUrl?: string;
  onCancel: () => void;
  /** The press merged or raised its request; the window is done. */
  onMerged: (answer: MergeSetAnswer) => void;
}) {
  const company = recordType === 'CompanyRecord';
  /** 🔒 Items merge on their own facts and their own refusal (decision 7470): two in different units cannot merge, and the window says so before the press. */
  const items = recordType === 'Item';
  /** The first read names no survivor: it answers the records and the survivor proposed, which no later choice of survivor changes. */
  const first = useApi<MergeSetPreviewAnswer>(mergeSetPreviewPath(recordType, ids, null), apiBaseUrl);
  const base = first.state.kind === 'ready' ? first.state.data : null;
  const [chosen, setChosen] = useState<string | null>(null);
  const keepId = chosen ?? base?.proposedSurvivorId ?? null;
  // A survivor chosen other than the proposed one is read against itself, and the first read answers the proposed one.
  const other = useApi<MergeSetPreviewAnswer>(base !== null && keepId !== null && keepId !== base.proposedSurvivorId ? mergeSetPreviewPath(recordType, ids, keepId) : null, apiBaseUrl);
  const preview = base !== null && keepId !== base.proposedSurvivorId ? other : first;
  const answer = preview.state.kind === 'ready' ? preview.state.data : null;
  // What moves is read against the survivor chosen now, and is not drawn from an answer read against another.
  const ready = answer && keepId !== null && answer.survivorId === keepId ? answer : null;
  const [picked, setPicked] = useState<{ keepId: string | null; picks: Readonly<Record<string, string>> }>({ keepId: null, picks: {} });
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  const records = useMemo(() => base?.records ?? [], [base]);
  const order = records.map((record) => record.facts.id);
  const conflicts = useMemo(() => (base ? mergeSetConflicts(recordType, base.records.map((record) => ({ id: record.facts.id, values: record.values }))) : []), [base, recordType]);
  // 🔒 Choosing another survivor picks its values afresh, as the pair's window does (decision 2501).
  const picks: Record<string, string> = Object.fromEntries(conflicts.map((conflict) => {
    const own = picked.keepId === keepId ? picked.picks[conflict.key] : undefined;
    return [conflict.key, own && mergeSetPickable(conflict, own) ? own : defaultSetPick(conflict, keepId ?? '', order)];
  }));
  const pick = (key: string, id: string) => setPicked({ keepId, picks: { ...(picked.keepId === keepId ? picked.picks : {}), [key]: id } });
  const takesFromMerged = Object.values(picks).some((id) => id !== keepId);
  const blockedBy = records.find((record) => record.facts.id !== keepId && record.facts.fixed) ?? null;
  const refusal = base?.refusal ?? null;

  const merge = async () => {
    if (keepId === null) return;
    setBusy(true);
    setRefused(null);
    const byRecord = setPicksByRecord(picks, keepId);
    const merged = order.filter((id) => id !== keepId).map((id) => ({ id, fromMerged: byRecord[id] ?? [] }));
    const result = await post<MergeSetAnswer>(items ? ITEM_MERGE_PATH : DUPLICATES_MERGE_SET_PATH, items ? { intoId: keepId, merged } : { recordType, intoId: keepId, merged }, apiBaseUrl);
    setBusy(false);
    if (!result.ok) {
      setRefused(result.message);
      return;
    }
    onMerged(result.data);
  };

  const itemFacts = (record: DuplicateRecordFacts, item: ItemMergeFacts) => (
    <dl className={styles.facts}>
      <dt>{t('duplicates.merge.itemType')}</dt>
      <dd>{item.typeLabel}</dd>
      <dt>{t('duplicates.merge.itemUnit')}</dt>
      <dd>{item.unitName}</dd>
      <dt>{t('duplicates.merge.itemOnHand')}</dt>
      <dd>{item.onHand ?? t('duplicates.none')}</dd>
      <dt>{t('duplicates.merge.itemLines')}</dt>
      <dd>{item.openLines}</dd>
      <dt>{t('duplicates.merge.created')}</dt>
      <dd>{record.createdOn}</dd>
    </dl>
  );
  const facts = (record: DuplicateRecordFacts) => (
    <dl className={styles.facts}>
      <dt>{company ? t('duplicates.merge.flags') : t('duplicates.merge.worksAt')}</dt>
      <dd>{flagsOf(record)}</dd>
      {company ? (
        <>
          <dt>{t('duplicates.merge.owner')}</dt>
          <dd>{record.ownerName ?? t('duplicates.none')}</dd>
          <dt>{t('duplicates.merge.sites')}</dt>
          <dd>{record.siteCount ?? 0}</dd>
          <dt>{t('duplicates.merge.documents')}</dt>
          <dd>{record.openDocuments ?? 0}</dd>
        </>
      ) : null}
      <dt>{t('duplicates.merge.created')}</dt>
      <dd>{record.createdOn}</dd>
      <dt>{t('duplicates.merge.createdBy')}</dt>
      <dd>{createdByOf(record)}</dd>
    </dl>
  );
  const note = (record: DuplicateRecordFacts) => (
    <>
      {record.id === base?.proposedSurvivorId ? <p className={styles.hint} data-merge-proposed="true">{t('duplicates.merge.proposed')}</p> : null}
      {record.fixed ? <p className={styles.hint} data-merge-fixed="true">{t('duplicates.merge.fixed')}</p> : null}
    </>
  );
  const rows = conflicts.map((conflict) => ({
    key: conflict.key,
    label: conflict.label,
    values: Object.fromEntries(records.map((record) => [record.facts.id, record.labels?.[conflict.key] ?? mergeFieldText(conflict.field, conflict.values[record.facts.id])])),
  }));
  const nameOf = (id: string) => records.find((record) => record.facts.id === id)?.facts.name ?? '';
  const firstRead = base === null ? first.state : null;

  return (
    <FormFrame
      host="window"
      open
      title={items ? t('duplicates.mergeSet.titleItem') : company ? t('duplicates.mergeSet.titleCompany') : t('duplicates.mergeSet.titlePerson')}
      onCancel={onCancel}
      width="document"
      dataForm="merge-set"
      act={{
        label: refusal ? t('duplicates.mergeSet.cannotMerge') : t('duplicates.action.merge'),
        onAct: merge,
        disabled: busy || refusal !== null || blockedBy !== null || ready === null,
        disabledReason: refusal ?? (blockedBy ? t('duplicates.mergeSet.fixedBlocked') : t('duplicates.merge.movesLoading')),
      }}
      refusal={refused}
      onDismissRefusal={() => setRefused(null)}
      typed={chosen !== null || Object.keys(picked.picks).length > 0}
    >
      <div className={styles.body} data-merge-set={ids.length}>
        <p>{items ? t('duplicates.mergeSet.leadItem') : t('duplicates.mergeSet.lead')}</p>
        {refusal ? <p className={styles.hint} data-merge-set-refusal="true">{refusal}</p> : null}
        {/* 🔒 Why Merge stands disabled, drawn as a sentence where the units sentence is, in sight above a long move list, and kept as the button's title (decision 0559; T3349). */}
        {blockedBy && refusal === null ? <p className={styles.hint} data-merge-set-blocked={blockedBy.facts.id}>{t('duplicates.mergeSet.fixedBlocked')}</p> : null}
        {/* 🔒 The fields this viewer may not see are absent from every column, and the one that stays keeps its own (decisions 0125, 7470). */}
        {base && (base.withheld?.length ?? 0) > 0 ? <p className={styles.hint} data-merge-set-withheld="true">{line(t('duplicates.mergeSet.itemWithheld'), (base.withheld ?? []).join(', '))}</p> : null}
        {firstRead?.kind === 'failed' ? <AlertBanner tone="error" message={firstRead.error} action={{ label: t('async.retry'), onAction: first.reload }} /> : null}
        {base === null ? (firstRead?.kind === 'failed' ? null : <p className={styles.hint}>{t('duplicates.mergeSet.loading')}</p>) : (
          <>
            <section className={styles.section} data-merge-fields={conflicts.length}>
              {conflicts.length > 0 ? <p className={styles.hint}>{t('duplicates.mergeSet.fieldsLead')}</p> : null}
              <MergeTable
                columns={records.map((record) => ({ id: record.facts.id, name: record.facts.name, facts: record.item ? itemFacts(record.facts, record.item) : facts(record.facts), note: note(record.facts) }))}
                keepId={keepId ?? ''}
                onKeep={setChosen}
                keepLabel={t('duplicates.merge.keep')}
                fieldLabel={t('duplicates.merge.fieldsTitle')}
                rows={rows}
                pickOf={(key) => picks[key] ?? keepId ?? ''}
                onPick={pick}
                pickable={(key, id) => {
                  const conflict = conflicts.find((one) => one.key === key);
                  return conflict ? mergeSetPickable(conflict, id) : false;
                }}
                empty={t('duplicates.mergeSet.fieldsNone')}
                disabled={busy}
              />
              {ready && ready.goesToApprover ? (
                <p className={styles.hint} data-merge-approval-note="true" data-merge-approvers={ready.approvalRoutes.length}>
                  {ready.approvalRoutes.length > 1 ? t('duplicates.mergeSet.twoApprovers') : takesFromMerged ? t('duplicates.mergeSet.fieldsApproval') : t('duplicates.mergeSet.approval')}
                </p>
              ) : null}
            </section>
            <section className={styles.section} data-merge-moves-state={ready ? 'ready' : preview.state.kind}>
              <h3 className={styles.sideTitle}>{t('duplicates.merge.movesTitle')}</h3>
              {preview.state.kind === 'failed' ? <AlertBanner tone="error" message={preview.state.error} action={{ label: t('async.retry'), onAction: preview.reload }} /> : null}
              {ready === null ? (preview.state.kind === 'failed' ? null : <p className={styles.hint}>{t('duplicates.merge.movesLoading')}</p>) : ready.moves.map((from) => (
                <div key={from.losingId} className={setStyles.from} data-merge-set-from={from.losingId}>
                  <h4 className={setStyles.fromTitle}>{nameOf(from.losingId)}</h4>
                  <div className={styles.moves}>
                    {from.moves.map((list) => (
                      <div key={list.kind} className={styles.moveList} data-merge-moves={list.kind} data-merge-moves-count={list.names.length + list.more}>
                        <h5 className={styles.moveTitle}>{line(MOVE_TITLES[list.kind], '·', list.names.length + list.more)}</h5>
                        {list.names.length === 0 ? <p className={styles.hint}>{t('duplicates.merge.movesNone')}</p> : (
                          <ul className={styles.names}>
                            {list.names.map((name) => <li key={name.id} data-merge-move={name.id}>{name.label}</li>)}
                          </ul>
                        )}
                        {list.more > 0 ? <p className={styles.hint} data-merge-moves-more={list.more}>{line(list.more, t('duplicates.merge.movesMore'))}</p> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          </>
        )}
      </div>
    </FormFrame>
  );
}
